<script setup>
import BidsTable from '@/components/table/Table.vue';
import BaseCardTable from '@/components/table/BaseCardTable.vue';
import CheckoutModal from '@/components/modals/CheckoutModal.vue';
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';

const quickFilterText = ref('');
const showCheckoutModal = ref(false);
const buyActive = ref(null);
const serviceFee = ref(0);
const loading = ref(false);
const bids = ref([]);
const rowData = ref([]);
const cardData = ref([]);

const currentPage = ref(1);

const filteredData = computed(() => {
    if (!quickFilterText.value) return rowData.value;
    
    const searchTerm = quickFilterText.value.toLowerCase();
    return rowData.value.filter(item => 
        Object.values(item).some(value => 
            String(value).toLowerCase().includes(searchTerm)
        )
    );
});

const sortedData = computed(() => {
    if (!sortBy.value) return filteredData.value;
    
    return [...filteredData.value].sort((a, b) => {
        let aValue = a[sortBy.value];
        let bValue = b[sortBy.value];
        
        // Handle date sorting
        if (sortBy.value.includes('date') || sortBy.value === 'created_at') {
            aValue = new Date(aValue).getTime();
            bValue = new Date(bValue).getTime();
        }
        
        // Handle amount sorting
        if (sortBy.value === 'amount') {
            aValue = parseFloat(String(aValue).replace(/[^0-9.-]/g, ''));
            bValue = parseFloat(String(bValue).replace(/[^0-9.-]/g, ''));
        }
        
        if (aValue < bValue) return sortOrder.value === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder.value === 'asc' ? 1 : -1;
        return 0;
    });
});

const totalItems = computed(() => filteredData.value.length);
const itemsPerPage = ref(10);
const sortBy = ref('created_at');
const sortOrder = ref('desc');

onMounted(async () => {
    loading.value = true;
    await setData();
    loading.value = false;
});

const getBadgeConfig = (bid) => {
    const status = bid.status;
    if(bid.sale) {
        const lastHistory = getLastHistory(bid);
        const statusToCheck = ['paid', 'ready'];
        if(!statusToCheck.includes(lastHistory.status.name) && status === 'ACCEPTED') {
            return { text: 'Awaiting Credentials', type: 'warning' };
        }
    }
    switch (status) {
        case 'PENDING':
            return { text: 'In Review', type: 'warning' };
        case 'ACCEPTED':
            return { text: 'Accepted', type: 'success' };
        case 'REJECTED':
            return { text: 'Rejected', type: 'error' };
        case 'EXPIRED':
            return { text: 'Expired', type: 'error' };
        case 'PAID':
            return { text: 'Paid', type: 'success' };
        default:
            return { text: status, type: 'default' };
    }
};



const getLastHistory = (params) => {
    if(!params.sale) return null;
    return params.sale.history.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
}

const isDisabledButton = (params) => {
    if(params.sale) {
        const lastHistory = getLastHistory(params);
        return lastHistory.status.name !== 'ready';
    }

    return params.status !== 'ACCEPTED';
}

const handlePayment = async () => {
    showCheckoutModal.value = false;
    await setData();
}

const handleIssuePayment = (item) => {
    if (!isDisabledButton(item)) {
        showCheckoutModal.value = true;
        buyActive.value = item;

        const bid = bids.value.find(bid => bid.id === item.id);
        if(bid) {
            buyActive.value.seller_account_id = bid?.seller?.stripe_account_id;
            buyActive.value.payment_methods = bid?.listing?.payment_methods;
        }
        
        serviceFee.value = 0;
    }
}

const handleSort = (column) => {
    if (sortBy.value === column) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = column;
        sortOrder.value = 'asc';
    }
}

const setData = async () => {
    const response = await axios.get('/api/bids/my-bids');
    bids.value = response.data;
    rowData.value = bids.value.map(bid => {
        return {
            id: bid?.id,
            dataset_name: bid?.listing?.datasets[0]?.name,
            dataset_tooltip: bid?.listing?.datasets[0]?.name,
            dataset_external_source: bid?.listing?.datasets[0]?.external_source,
            sale: bid?.sale,
            listing_name: bid?.listing?.name,
            listing_id: bid?.listing?.id,
            receipt_address: bid?.listing?.receipt_address,
            created_at: bid?.created_at,
            expiration_date: bid?.expires_at,
            amount: bid?.bid_amount,
            currency: bid?.currency,
            status: bid?.status,
            seller: bid?.seller?.user?.username,
            seller_tooltip: bid?.seller?.user?.username,
            seller_id: bid?.seller?.user?.id,
            fee: bid?.listing?.fee
        }
    });
    cardData.value = bids.value.map(bid => ({
        id: bid.id,
        title: bid?.listing?.name,
        rows: [
            { column: 'Seller', type: 'text', value: bid?.seller?.user?.username },
            { column: 'Dataset', type: 'text', value: bid?.listing?.datasets[0]?.name },
            { column: 'Created', type: 'date', value: bid?.created_at },
            { column: 'Expires', type: 'date', value: bid?.expires_at },
            { column: 'Amount', type: 'text', value: bid?.bid_amount},
            { column: 'Status', type: 'tag', value: getBadgeConfig(bid).text, color: getBadgeConfig(bid).type },
    ]
    }));

}

const columns = [
    {
        key: 'dataset_name',
        label: 'DATASET',
        type: 'text',
        sortable: true,
        align: 'left'
    },
    { 
        key: 'created_at',
        label: 'BID SUBMITTED', 
        type: 'date',
        sortable: true,
        align: 'left'
    },
    { 
        key: 'expiration_date',
        label: 'EXPIRATION', 
        type: 'date',
        sortable: true,
        align: 'left'
    },
    {
        key: 'seller',
        label: 'SELLER',
        type: 'text',
        sortable: true,
        align: 'left'
    },
    { 
        key: 'amount',
        label: 'AMOUNT', 
        type: 'amount',
        sortable: true,
        align: 'left'
    },
    {
        key: 'status',
        label: 'STATUS', 
        type: 'text',
        sortable: true,
        align: 'left'
    },
    { 
        key: 'action',
        label: 'ACTION',
        type: 'text',
        sortable: false,
        align: 'center'
    },
];

</script>

<template>
    <CheckoutModal v-model:showCheckoutModal="showCheckoutModal" :buyActive="buyActive" :serviceFee="serviceFee" @payment-success="handlePayment" @finish="handlePayment" v-if="showCheckoutModal" :isBid="true" />
    <div>
        <BaseCardTable :items="cardData" :quickFilterText="quickFilterText" :isLoading="loading" class="md:hidden"/>
        <BidsTable 
            :data="sortedData" 
            :columns="columns" 
            :totalItems="totalItems"
            :searchQuery="quickFilterText"
            @update:searchQuery="quickFilterText = $event"
            :currentPage="currentPage"
            @update:currentPage="currentPage = $event"
            :itemsPerPage="itemsPerPage"
            @update:itemsPerPage="itemsPerPage = $event"
            :sortBy="sortBy"
            :sortOrder="sortOrder"
            @sort="handleSort"
            title="My Bids"
            searchPlaceholder="Search bids..."
            emptyMessage="No bids found"
            class="hidden md:block"
        >
            <template #column-dataset_name="{ item }">
                <router-link :to="`/item-detail/${item.listing_id}`" class="cursor-pointer font-semibold hover:text-echoes-primary transition-colors duration-200 w-fit truncate max-w-[200px]">
                    {{ item.dataset_name }}
                </router-link>
            </template>
            
         
            
            <template #column-status="{ item }">
                <span :class="[
                    'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                    getBadgeConfig(item).type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500' :
                    getBadgeConfig(item).type === 'warning' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500' :
                    getBadgeConfig(item).type === 'error' ? 'bg-red-500/20 text-red-400 border border-red-500' :
                    'bg-gray-500/20 text-gray-400 border border-gray-500'
                ]">
                    {{ getBadgeConfig(item).text }}
                </span>
            </template>
            
            <template #column-action="{ item }">
                <button 
                    @click="handleIssuePayment(item)"
                    :disabled="isDisabledButton(item)"
                    :class="[
                        'flex items-center gap-2 px-2.5 py-1 text-gray-100 rounded-md transition-colors duration-200',
                        isDisabledButton(item) ? 'bg-gray-500 opacity-40 cursor-not-allowed' : 'bg-echoes-primary hover:bg-echoes-primary/80'
                    ]"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Issue Payment
                </button>
            </template>
        </BidsTable>
    </div>
</template>